import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TabsModule } from 'primeng/tabs';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { catchError, of } from 'rxjs';
import { DocumentAdminService } from '../../../core/services/document-admin.service';
import { AuthService } from '../../../core/services/auth.service';
import { DocumentResponse } from '../../../core/models';
import { AppEmptyState } from '../../../shared/ui/empty-state/app-empty-state';
import { AppStatusTag, StatusTagSeverity } from '../../../shared/ui/status-tag/app-status-tag';
import { DocumentRejectDialogComponent } from '../reject/document-reject-dialog';

@Component({
  selector: 'app-document-list',
  standalone: true,
  imports: [CommonModule, TabsModule, ButtonModule, TableModule,
    ProgressSpinnerModule, TooltipModule, ToastModule,
    AppEmptyState, AppStatusTag, DocumentRejectDialogComponent],
  templateUrl: './document-list.html',
  styleUrl: './document-list.scss'
})
export class DocumentListComponent implements OnInit {
  private svc   = inject(DocumentAdminService);
  private toast = inject(MessageService);
  authService   = inject(AuthService);

  activeTab = signal('0');

  // ── Onglet "En attente" (PENDING_VALIDATION + ERROR) ─────────────────────────
  loadingPending = signal(true);
  pendingDocs    = signal<DocumentResponse[]>([]);
  processing     = signal<string | null>(null);

  rejectDialogVisible = signal(false);
  rejectTarget         = signal<DocumentResponse | null>(null);
  rejectText            = '';

  // ── Onglet "Validés" (chargé au premier affichage de l'onglet) ───────────────
  loadingValidated = signal(false);
  validatedDocs    = signal<DocumentResponse[]>([]);
  private validatedLoaded = false;

  ngOnInit() {
    this.loadPending();
  }

  private loadPending() {
    this.loadingPending.set(true);
    this.svc.getPendingDocuments().pipe(catchError(() => of([]))).subscribe(docs => {
      this.pendingDocs.set(docs);
      this.loadingPending.set(false);
    });
  }

  private loadValidated() {
    this.loadingValidated.set(true);
    this.svc.getDocumentsByStatus('VALIDATED').pipe(catchError(() => of([]))).subscribe(docs => {
      this.validatedDocs.set(docs);
      this.loadingValidated.set(false);
      this.validatedLoaded = true;
    });
  }

  onTabChange(value: string | number | undefined) {
    const v = String(value ?? '0');
    this.activeTab.set(v);
    if (v === '1' && !this.validatedLoaded) this.loadValidated();
  }

  countByStatus(status: string): number {
    return this.pendingDocs().filter(d => d.documentStatus === status).length;
  }

  openDoc(docId: string) {
    this.svc.getDocumentUrl(docId).pipe(catchError(() => of(''))).subscribe(url => {
      if (url) window.open(url, '_blank');
    });
  }

  validate(doc: DocumentResponse) {
    this.processing.set(doc.id);
    this.svc.validateDocument(doc.id).subscribe({
      next: () => {
        this.pendingDocs.update(docs => docs.filter(d => d.id !== doc.id));
        this.toast.add({ severity: 'success', summary: 'Document validé', life: 3500 });
        this.processing.set(null);
      },
      error: () => {
        this.toast.add({ severity: 'error', summary: 'Erreur', detail: 'La validation a échoué.', life: 5000 });
        this.processing.set(null);
      }
    });
  }

  startReject(doc: DocumentResponse) {
    this.rejectTarget.set(doc);
    this.rejectText = '';
    this.rejectDialogVisible.set(true);
  }

  cancelReject() {
    this.rejectDialogVisible.set(false);
    this.rejectTarget.set(null);
  }

  confirmReject() {
    const doc = this.rejectTarget();
    if (!doc || !this.rejectText.trim()) return;
    this.processing.set(doc.id);
    // Même remarque que validate() : PATCH .../reject renvoie 204, `null` n'est pas une
    // sentinelle d'erreur fiable ici.
    this.svc.rejectDocument(doc.id, this.rejectText).subscribe({
      next: () => {
        this.pendingDocs.update(docs => docs.filter(d => d.id !== doc.id));
        this.toast.add({ severity: 'warn', summary: 'Document rejeté', life: 3500 });
        this.processing.set(null);
        this.rejectDialogVisible.set(false);
        this.rejectTarget.set(null);
      },
      error: () => {
        this.toast.add({ severity: 'error', summary: 'Erreur', detail: 'Le rejet a échoué.', life: 5000 });
        this.processing.set(null);
        this.rejectDialogVisible.set(false);
        this.rejectTarget.set(null);
      }
    });
  }

  statusLabel(s: string): string {
    const map: Record<string, string> = {
      VALIDATED: 'Validé', REJECTED: 'Rejeté', PENDING_VALIDATION: 'En attente', ERROR: 'Erreur scan'
    };
    return map[s] ?? s;
  }

  statusSeverity(s: string): StatusTagSeverity {
    const map: Record<string, StatusTagSeverity> = {
      VALIDATED: 'success', REJECTED: 'danger', PENDING_VALIDATION: 'warn', ERROR: 'danger'
    };
    return map[s] ?? 'secondary';
  }

  docTypeLabel(t: string = ''): string {
    const map: Record<string, string> = {
      DOCUMENT_IDENTITE: "Pièce d'identité",
      PHOTO_PROFIL:      'Photo de profil',
      LOGO:              'Logo agence',
    };
    return map[t] ?? (t.replace(/_/g, ' ') || 'Document');
  }

  docIcon(t: string = ''): string {
    const map: Record<string, string> = {
      DOCUMENT_IDENTITE: 'pi-id-card',
      PHOTO_PROFIL:      'pi-user',
      LOGO:              'pi-building',
    };
    return map[t] ?? 'pi-file';
  }

  ownerName(doc: DocumentResponse): string {
    const name = [doc.ownerFirstName, doc.ownerLastName].filter(Boolean).join(' ');
    return name || '—';
  }

  ownerInitials(doc: DocumentResponse): string {
    const a = (doc.ownerFirstName || '?')[0] ?? '?';
    const b = (doc.ownerLastName || '')[0] ?? '';
    return (a + b).toUpperCase();
  }
}
